/**
 * Update Store
 * Manages application update state for stale chunk detection
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface VersionInfo {
  buildId: string;
  version: string;
  timestamp: string;
  env: string;
}

interface UpdateState {
  // Update detection
  updateRequired: boolean;
  newVersion: VersionInfo | null;
  oldVersion: VersionInfo | null;
  updateDetectedAt: number | null;

  // User interaction
  userDismissed: boolean;
  dismissedAt: number | null;

  // Actions
  setUpdateRequired: (required: boolean, newVersion?: VersionInfo, oldVersion?: VersionInfo) => void;
  dismissUpdate: () => void;
  resetUpdate: () => void;
  shouldShowNotification: () => boolean;
}

export const useUpdateStore = create<UpdateState>()(
  persist(
    (set, get) => ({
      // Initial state
      updateRequired: false,
      newVersion: null,
      oldVersion: null,
      updateDetectedAt: null,
      userDismissed: false,
      dismissedAt: null,

      // Set update required
      setUpdateRequired: (required, newVersion, oldVersion) => {
        console.log('[UpdateStore] 📝 setUpdateRequired called:', {
          required,
          newVersion,
          oldVersion
        });

        set({
          updateRequired: required,
          newVersion: newVersion || null,
          oldVersion: oldVersion || null,
          updateDetectedAt: required ? Date.now() : null,
          // Reset dismissal when new update is detected
          userDismissed: false,
          dismissedAt: null
        });

        console.log('[UpdateStore] ✅ State updated:', get());
      },

      // User dismissed the update notification
      dismissUpdate: () => {
        set({
          userDismissed: true,
          dismissedAt: Date.now()
        });
      },

      // Reset update state (after refresh)
      resetUpdate: () => {
        set({
          updateRequired: false,
          newVersion: null,
          oldVersion: null,
          updateDetectedAt: null,
          userDismissed: false,
          dismissedAt: null
        });
      },

      // Check if notification should be shown
      shouldShowNotification: () => {
        const state = get();

        // Don't show if no update required
        if (!state.updateRequired) return false;

        // Don't show if user dismissed
        if (state.userDismissed) return false;

        // Show the notification
        return true;
      }
    }),
    {
      name: 'cordiant-update-storage',
      partialize: (state) => ({
        // Only persist dismissal state, not the update itself
        // (update state should be fresh on each load)
        userDismissed: state.userDismissed,
        dismissedAt: state.dismissedAt
      })
    }
  )
);

export default useUpdateStore;
